import { getCustomerDetails, logout } from '../actions/customerData';
import { CustomerAddress } from '../types/address';
import { SimpleUser, CustomerRoleChild, loginCustomer, AvailableUsers, Customer } from '../types/customer';

const prepChildDetails = (childUsers: SimpleUser[], children: CustomerRoleChild[], endpoint: string): SimpleUser[] => {
  // We set the array of available users.
  if (children) {
    for (const child of children) {
      // Companies don't have first names so we check if it exists.
      let childName = child.customerLastNameChild;
      if (child.customerFirstNameChild) {
        childName = `${child.customerFirstNameChild} ${child.customerLastNameChild}`;
      }

      const childUser: SimpleUser = {
        userId: child.customerIdChild,
        userName: childName,
        userFirstName: child.customerFirstNameChild,
        userLastName: child.customerLastNameChild,
        userAddress: child.address ?? [],
        endpoint: endpoint,
        userNumber: child.mobileNumber,
        userEmail: child.email,
        customerType: child.customerTypeChild ? child.customerTypeChild.toLowerCase() : 'private',
        customerRoleTypeNo: child.customerRoleType.customerRoleTypeNo,
      };

      // Make sure we don't add duplicate users.
      const childIds = childUsers.map((child) => child.userId + child.endpoint);
      if (!childIds.includes(childUser.userId + endpoint) && child.customerRoleType.customerRoleTypeNo !== 83) {
        childUsers.push(childUser);
      }
    }
  }
  return childUsers;
};

export const setUserDetails = async (userDetails: loginCustomer, availableUsers: AvailableUsers): Promise<AvailableUsers> => {
  const preppedAddress: Array<CustomerAddress> = [];
  let parentUser: SimpleUser;
  let children: SimpleUser[] = [];

  let childRoles: CustomerRoleChild[] = [];

  // If there are user details we will set them.
  // In some cases the user may be fake (uid 0). We still want these users to be able to login.
  if (userDetails && userDetails.customerId !== undefined) {
    const userdata: Customer | undefined = await getCustomerDetails(userDetails.customerId, userDetails.customerId);

    // Companies don't have first names so we check if it exists.
    let name = userdata?.lastName ?? '';
    if (userdata?.firstName) {
      name = `${userdata.firstName} ${userdata.lastName}`;
    }

    if (name === '' && userDetails.name) {
      name = userDetails.name;
    }

    parentUser = {
      loginEmail: userDetails.email,
      loginName: userDetails.name,
      loginPhone: userdata?.mobileNumber,
      userId: userDetails.customerId,
      userName: name,
      userFirstName: userdata?.firstName ?? '',
      userLastName: userdata?.lastName ?? '',
      userAddress: userdata?.addresses ?? [],
      userEmail: userdata?.email,
      userNumber: userdata?.mobileNumber,
      endpoint: 'ret',
    };

    childRoles = userdata?.customerRoleChild ?? [];

    if (userdata && userdata.customerType) {
      parentUser.customerType = userdata.customerType.toLowerCase();
    }
  }
  // If we have no information on the user then we simple remove the token and reload the page.
  // There is probably a malformed/malicious token if we get here.
  else {
    // No user set, remove the token and reload the page.
    sessionStorage.removeItem('token');
    if (window.top) {
      window.top.location.reload();
    }

    // Even though we are reloading the page, typescript gets anoyed if we don't have
    // something set for this.
    parentUser = {
      userId: 0,
      userName: '',
      userFirstName: '',
      userLastName: '',
      userAddress: preppedAddress,
      userEmail: '',
      userNumber: '',
      endpoint: '',
    };
  }

  // We do want all child users available.
  if (childRoles) {
    children = prepChildDetails(children, childRoles, 'ret');
  }

  availableUsers = {
    parentUser,
    activeUser: parentUser,
    childUsers: children,
  };

  return availableUsers;
};

export const inIframe = (singleComponent = false): boolean => {
  if (!singleComponent) {
    try {
      return window.self !== window.top;
      // eslint-disable-next-line
    } catch (e) {
      return true;
    }
  }
  return false;
};

export const logUserOut = (): void => {
  logout();

  // We clear the whole session storage because Eliq also stores some stuff in it
  // and that was causing translation issues when logging back in.
  const token = sessionStorage.getItem('keycloakIdToken');
  sessionStorage.clear();

  const OmaLummeLocalStorageItems = [
    'AlertBannerDisplayed',
    'PriceFixBannerDisplayed',
    'OfferBannerDismissDate',
    'OomiLandingPageBannerDisplayed',
    'OomiBillingBannerDisplayed',
    'EvChargingTag',
    'EvChargingSite',
    'EvChargingChargerSocket',
  ];
  // remove all items from localStorage that are not in the list above
  for (const key in localStorage) {
    if (OmaLummeLocalStorageItems.indexOf(key) === -1) {
      localStorage.removeItem(key);
    }
  }

  if (import.meta.env.VITE_LOGOUT_URL && token) {
    window.location.href = `${import.meta.env.VITE_LOGOUT_URL}&id_token_hint=${token}`;
  }
};

/**
 * Function to return token and split if it is too long.
 */
export const getUserToken = (): { bearer: string; content: string } => {
  const token = sessionStorage.getItem('token');
  let bearer = '';
  let content = '';
  if (token && token?.length > 5000) {
    bearer = token?.substring(0, 5000);
    content = token?.substring(5000, token?.length);
  } else if (token) {
    bearer = token;
  }

  return { bearer, content };
};
